import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Copy, KeyRound } from 'lucide-react';
import { useAuth, useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', red: '#B0413E', line: '#E4E9EF' };

export const MfaSetupCard = ({ forced = false, onEnabled }) => {
  const api = useApi();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [prov, setProv] = useState(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/auth/mfa/status'); setStatus(r.data); } catch { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true);
    try { const r = await api.post('/auth/mfa/setup'); setProv(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Setup failed'); }
    setBusy(false);
  };

  const enable = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error('Enter the 6-digit code from your authenticator app');
    setBusy(true);
    try {
      const r = await api.post('/auth/mfa/enable', { code });
      setRecovery(r.data.recovery_codes);
      toast.success('MFA enabled');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invalid code'); }
    setBusy(false);
  };

  const finish = () => { onEnabled ? onEnabled() : window.location.reload(); };

  if (!status) return <div className="text-xs" style={{ color: C.slate }}>Loading…</div>;

  if (recovery) return (
    <div className="space-y-3" data-testid="mfa-recovery-codes">
      <div className="text-sm font-semibold" style={{ color: C.green }}><ShieldCheck size={15} className="inline mr-1" />MFA is enabled. Save your recovery codes — shown once only.</div>
      <div className="grid grid-cols-2 gap-1 p-3 rounded border font-mono text-xs" style={{ borderColor: C.line, background: '#FAFBFC' }}>
        {recovery.map(c => <div key={c}>{c}</div>)}
      </div>
      <div className="flex gap-2">
        <button onClick={() => { navigator.clipboard.writeText(recovery.join('\n')); toast.success('Copied'); }} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold border" style={{ borderColor: C.line, color: C.navy }} data-testid="mfa-copy-recovery-btn"><Copy size={12} /> Copy codes</button>
        <button onClick={finish} className="px-3 py-1.5 rounded text-xs font-semibold text-white" style={{ background: C.navy }} data-testid="mfa-done-btn">I've saved them — continue</button>
      </div>
    </div>
  );

  if (status.enabled) return (
    <div className="space-y-2" data-testid="mfa-active-state">
      <div className="text-sm font-semibold" style={{ color: C.green }}><ShieldCheck size={15} className="inline mr-1" />MFA active since {status.enrolled_at?.slice(0, 10)}</div>
      <div className="text-xs" style={{ color: C.slate }}>{status.recovery_codes_remaining} recovery codes remaining.{status.enforced && ' MFA is enforced for your role and cannot be disabled.'}</div>
    </div>
  );

  if (prov) return (
    <div className="space-y-3" data-testid="mfa-enroll-step">
      <div className="text-xs" style={{ color: C.slate }}>Scan with Google Authenticator / Authy, or enter the key manually, then confirm with a 6-digit code.</div>
      <div className="flex items-start gap-4">
        <img src={prov.qr_data_url} alt="MFA QR code" className="w-40 h-40 border rounded" style={{ borderColor: C.line }} data-testid="mfa-qr-image" />
        <div className="space-y-2">
          <div className="text-[10px] uppercase font-semibold" style={{ color: C.slate }}>Manual key</div>
          <code className="text-xs px-2 py-1 rounded" style={{ background: '#EEF1F5' }} data-testid="mfa-manual-key">{prov.manual_key}</code>
          <div className="text-[10px] uppercase font-semibold pt-2" style={{ color: C.slate }}>6-digit code</div>
          <div className="flex gap-2">
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="w-28 border rounded-sm px-2 py-1.5 text-sm font-mono tracking-widest" style={{ borderColor: C.line }} data-testid="mfa-enable-code-input" />
            <button onClick={enable} disabled={busy} className="px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.green }} data-testid="mfa-enable-btn">Verify & enable</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="mfa-start-state">
      <div className="text-sm" style={{ color: C.navy }}>
        <KeyRound size={15} className="inline mr-1" />
        {forced
          ? `MFA is enforced for ${user?.role === 'platform_owner' ? 'platform owners' : 'administrators'} — set up your authenticator to continue.`
          : 'Add a second factor (TOTP authenticator app) to your sign-in.'}
      </div>
      <button onClick={start} disabled={busy} className="px-4 py-2 rounded-md text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.navy }} data-testid="mfa-start-setup-btn">Start MFA setup</button>
    </div>
  );
};

export default MfaSetupCard;
